import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, X, Calendar, Home, User, CreditCard, XCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('user_token');
      if (!token) return;

      const response = await axios.get(
        `${API_BASE}/notifications/unread-count`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('user_token');
      if (!token) return;

      const response = await axios.get(
        `${API_BASE}/notifications`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('user_token');
      await axios.post(
        `${API_BASE}/notifications/${notificationId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update local state
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('user_token');
      await axios.post(
        `${API_BASE}/notifications/mark-all-read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  const getNotificationIcon = (type) => {
    const PINE = '#1F5C42';
    const NAVY = '#1E3F70';
    const PINK = '#FF5C8A';
    const CORAL = '#B91C1C';
    const icons = {
      booking_confirmed: <Calendar className="w-5 h-5" style={{ color: PINE }} />,
      booking_cancelled: <XCircle className="w-5 h-5" style={{ color: CORAL }} />,
      booking_request: <AlertCircle className="w-5 h-5" style={{ color: NAVY }} />,
      cancellation_approved: <CheckCheck className="w-5 h-5" style={{ color: PINE }} />,
      cancellation_denied: <X className="w-5 h-5" style={{ color: CORAL }} />,
      host_approved: <User className="w-5 h-5" style={{ color: PINE }} />,
      property_approved: <Home className="w-5 h-5" style={{ color: NAVY }} />,
      payment_received: <CreditCard className="w-5 h-5" style={{ color: PINE }} />,
      new_review: <AlertCircle className="w-5 h-5" style={{ color: PINK }} />,
      default: <Bell className="w-5 h-5 text-gray-600" />
    };
    return icons[type] || icons.default;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-sm"
            style={{ backgroundColor: '#1F5C42' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel — on mobile we anchor to the viewport (fixed) so the
              384px-wide dropdown can't overflow off the screen edge under
              a bell button that sits in the far-right of the header. On
              `sm:` and up, the original absolute-right-0 behavior remains. */}
          <Card className="fixed sm:absolute top-16 sm:top-auto right-2 sm:right-0 left-2 sm:left-auto sm:mt-2 sm:w-96 max-h-[80vh] sm:max-h-[600px] overflow-hidden shadow-xl z-50" data-testid="notification-panel">
            {/* Header */}
            <div
              className="p-4 border-b border-gray-200"
              style={{ background: 'linear-gradient(135deg, #1F5C42 0%, #2E8270 100%)' }}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notifications
                </h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-white/90 hover:text-white underline underline-offset-2 font-medium"
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[500px]">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300 animate-pulse" />
                  <p>Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No notifications yet</p>
                  <p className="text-sm mt-1">We'll notify you of important updates</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="p-4 border-b border-gray-100 cursor-pointer transition-colors hover:brightness-95"
                    style={{
                      backgroundColor: notification.read ? '#ffffff' : 'rgba(31, 92, 66, 0.06)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4
                            className="font-semibold text-sm"
                            style={{ color: notification.read ? '#374151' : '#1E3F70' }}
                          >
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <div
                              className="flex-shrink-0 w-2 h-2 rounded-full mt-1"
                              style={{ backgroundColor: '#1F5C42' }}
                            />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-2">{formatTime(notification.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    setIsOpen(false);
                  }}
                  className="text-sm font-semibold hover:brightness-110 transition"
                  style={{ color: '#1F5C42' }}
                >
                  View all notifications
                </button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
